/**
 * Glossary terms + DefinedTermSet schema.
 * Data-only module so the /glossary route head() can server-render schema.
 */
const BASE_URL = "https://leadxkids.com";

export interface Term {
  id: string;
  term: string;
  definition: string;
}

export const terms: Term[] = [
  {
    id: "sel",
    term: "Social Emotional Learning (SEL)",
    definition:
      "A framework for teaching children to recognise emotions, regulate behaviour, build relationships and make responsible decisions. Used worldwide as a core part of life-skills education for children aged 6-16.",
  },
  {
    id: "confidence",
    term: "Child Confidence",
    definition:
      "A child's willingness to act, speak and try in the face of uncertainty or judgement. Confidence is built through repeated low-stakes wins, not pep talks. It is measurable across speaking-up, judgement-handling, recovery from failure, and social initiation.",
  },
  {
    id: "growth-mindset",
    term: "Growth Mindset",
    definition:
      "Carol Dweck's concept that abilities can be developed through effort and learning, in contrast to a fixed mindset where abilities are seen as innate. Children with a growth mindset are more likely to persist through setbacks.",
  },
  {
    id: "resilience",
    term: "Resilience",
    definition:
      "The ability of a child to recover from setbacks, criticism or failure without lasting emotional damage. Resilience is a skill, not a personality trait, and improves with structured practice.",
  },
  {
    id: "executive-function",
    term: "Executive Function",
    definition:
      "Cognitive skills that govern focus, working memory, impulse control and task switching. Weak executive function looks like distractibility, forgetfulness and difficulty starting work; it strengthens with deliberate practice between ages 8-14.",
  },
  {
    id: "public-speaking-for-kids",
    term: "Public Speaking for Kids",
    definition:
      "Age-appropriate training in classroom participation, presentation structure and stage comfort for children aged 8-14. It is taught as a skill stack: structured thinking, physical composure, and recovery from mistakes.",
  },
  {
    id: "personality-assessment-for-kids",
    term: "Child Personality Assessment",
    definition:
      "A structured measurement of stable traits (such as introversion, conscientiousness, openness) in children, paired with behaviour-level confidence indicators. Unlike adult tests it is designed to inform a growth plan, not a fixed label.",
  },
  {
    id: "life-skills",
    term: "Life Skills",
    definition:
      "Practical skills outside the academic curriculum that determine real-world success: confidence, communication, decision-making, focus, resilience, empathy and friendship-building. Recognised by WHO and NEP 2020 (India) as essential.",
  },
  {
    id: "stage-fear",
    term: "Stage Fear (in children)",
    definition:
      "Anxiety about speaking or performing in front of an audience. In children 8-14 it usually reflects a judgement-handling gap rather than a fear of the audience itself, and responds well to graded exposure.",
  },
  {
    id: "self-esteem",
    term: "Self-Esteem vs Self-Confidence",
    definition:
      "Self-esteem is how a child feels about who they are. Self-confidence is how a child rates their ability to do specific things. The two are correlated but distinct; LeadXkids primarily measures and builds self-confidence.",
  },
  {
    id: "future-readiness",
    term: "Future Readiness",
    definition:
      "The set of cognitive, social and emotional capabilities a child will need to thrive in a world where information access is commoditised — including critical thinking, communication, adaptability, and self-direction.",
  },
  {
    id: "child-psychologist",
    term: "Child Psychologist",
    definition:
      "A licensed mental-health professional specialised in child development, behaviour and emotional well-being. In India, qualified practitioners are registered with the Rehabilitation Council of India (RCI) or hold equivalent clinical credentials.",
  },
];

export const definedTermSetJsonLd = {
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  name: "LeadXkids Glossary: Confidence, Life Skills & Child Development",
  url: `${BASE_URL}/glossary`,
  inLanguage: "en-IN",
  hasDefinedTerm: terms.map((t) => ({
    "@type": "DefinedTerm",
    "@id": `${BASE_URL}/glossary#${t.id}`,
    name: t.term,
    description: t.definition,
    inDefinedTermSet: `${BASE_URL}/glossary`,
  })),
};

